import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Button, FontAwesomeIcon } from '@appcharge/shared-ui';
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { RollingOfferProductSequence } from 'common/contracts';
import { MIN_ROLLING_SUB_OFFERS } from 'constants/constants';
import { v4 as uuidv4 } from 'uuid';
import { TabsProps } from 'views/RollingOffers/types';

import RichText from 'components/RichText/RichText';
import { SortableItem } from 'components/SortableItem';

import SubOfferCard from './Card';

interface SubOfferWithId extends RollingOfferProductSequence {
    _sortId?: string;
}

const SubOffersList: React.FC<TabsProps> = ({
    formikProps,
    rollingOfferId,
    productsSequence,
    setProductsSequence
}) => {
    const { setFieldValue } = formikProps;

    const [openCardIndex, setOpenCardIndex] = useState<number | undefined>(undefined);
    const [activeId, setActiveId] = useState<string | null>(null);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    // Ensure each item has a unique sortable ID
    const itemsWithIds = useMemo(() => {
        return productsSequence.map((item, idx) => ({
            ...item,
            _sortId: (item as SubOfferWithId)._sortId || `sub-offer-${idx}-${uuidv4()}`
        }));
    }, [productsSequence]);

    const sortableIds = useMemo(
        () => itemsWithIds.map((item) => item._sortId),
        [itemsWithIds]
    );

    useEffect(() => {
        const needsIds = productsSequence.some((item) => !(item as SubOfferWithId)._sortId);
        if (needsIds && productsSequence.length > 0) {
            const withIds = productsSequence.map((item, idx) => ({
                ...item,
                _sortId: (item as SubOfferWithId)._sortId || `sub-offer-${idx}-${uuidv4()}`
            }));
            setProductsSequence(withIds);
        }

        return () => setOpenCardIndex(undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleDragStart = useCallback((event: DragStartEvent) => {
        const id = String(event.active.id);
        const index = itemsWithIds.findIndex((item) => item._sortId === id);
        setActiveId(id);
        setActiveIndex(index);
        setOpenCardIndex(undefined);
    }, [itemsWithIds]);

    const handleDragEnd = useCallback((event: DragEndEvent) => {
        const { active, over } = event;

        if (!over || active.id === over.id) {
            setActiveId(null);
            setActiveIndex(null);
            return;
        }

        const oldIndex = itemsWithIds.findIndex((item) => item._sortId === String(active.id));
        const newIndex = itemsWithIds.findIndex((item) => item._sortId === String(over.id));

        if (oldIndex === -1 || newIndex === -1) {
            setActiveId(null);
            setActiveIndex(null);
            return;
        }

        const newSequence = arrayMove(itemsWithIds, oldIndex, newIndex).map(
            (item, index) => ({ ...item, index: index + 1 })
        );

        setProductsSequence(newSequence);
        setFieldValue('productsSequence', newSequence, { shouldDirty: true });

        // Update open card index if needed
        setOpenCardIndex((prev) => {
            if (prev === undefined) return undefined;
            if (prev === oldIndex) return newIndex;
            if (oldIndex < prev && prev <= newIndex) return prev - 1;
            if (newIndex <= prev && prev < oldIndex) return prev + 1;
            return prev;
        });

        setActiveId(null);
        setActiveIndex(null);
    }, [itemsWithIds, setProductsSequence, setFieldValue]);

    const handleDragCancel = useCallback(() => {
        setActiveId(null);
        setActiveIndex(null);
    }, []);

    const handleToggleExpand = useCallback((index: number) => {
        setOpenCardIndex((prev) => (prev === index ? undefined : index));
    }, []);

    const handleDelete = useCallback((index: number) => {
        if (itemsWithIds.length <= MIN_ROLLING_SUB_OFFERS) return;

        const newSequence = itemsWithIds
            .filter((_, i) => i !== index)
            .map((item, i) => ({ ...item, index: i + 1 }));

        setProductsSequence(newSequence);
        setFieldValue('productsSequence', newSequence);

        setOpenCardIndex((prev) => {
            if (prev === undefined) return undefined;
            if (prev === index) return undefined;
            if (prev > index) return prev - 1;
            return prev;
        });
    }, [itemsWithIds, setProductsSequence, setFieldValue]);

    const handleAddSubOffer = useCallback(() => {
        const newSubOffer: SubOfferWithId = {
            index: itemsWithIds.length + 1,
            priceInUsdCents: 0,
            displayName: '',
            products: [{ product: { images: [] } }],
            _sortId: `sub-offer-new-${uuidv4()}`
        };
        const newSequence = [...itemsWithIds, newSubOffer];
        setProductsSequence(newSequence);
        setFieldValue('productsSequence', newSequence);
    }, [itemsWithIds, setProductsSequence, setFieldValue]);

    return (
        <div className="flex flex-col gap-9 pb-10">
            <RichText
                title="Configuration"
                description="Set 2 or more sub offers for the Rolling Offer. Each sub offer can contain up to 3 products."
            />

            <Button
                type="button"
                variant="outline"
                onClick={handleAddSubOffer}
                className="w-[186px] bg-base-muted border-none hover:bg-base-accent h-10"
            >
                <FontAwesomeIcon icon="fa-regular fa-circle-plus" className="mr-2 text-base" />
                Add New Sub Offer
            </Button>

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis]}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
            >
                <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                    <div className="flex flex-col gap-2">
                        {itemsWithIds.map((item, index) => (
                            <SortableItem key={item._sortId} id={item._sortId}>
                                <SubOfferCard
                                    formikProps={formikProps}
                                    rollingOfferId={rollingOfferId}
                                    productSequenceItem={item}
                                    sequenceIndex={index}
                                    productsSequence={itemsWithIds}
                                    setProductsSequence={setProductsSequence}
                                    isExpanded={openCardIndex === index}
                                    isCurrentItemDragged={activeId === item._sortId}
                                    onToggleExpand={() => handleToggleExpand(index)}
                                    onDelete={() => handleDelete(index)}
                                />
                            </SortableItem>
                        ))}
                    </div>
                </SortableContext>

                <DragOverlay>
                    {activeId && activeIndex !== null && itemsWithIds[activeIndex] && (
                        <div className="cursor-grabbing shadow-lg bg-white z-50 rounded-lg">
                            <SubOfferCard
                                formikProps={formikProps}
                                rollingOfferId={rollingOfferId}
                                productSequenceItem={itemsWithIds[activeIndex]}
                                sequenceIndex={activeIndex}
                                productsSequence={itemsWithIds}
                                setProductsSequence={setProductsSequence}
                                isExpanded={false}
                                isCurrentItemDragged={true}
                                onToggleExpand={() => { }}
                                onDelete={() => { }}
                            />
                        </div>
                    )}
                </DragOverlay>
            </DndContext>
        </div>
    );
};

export default SubOffersList;

